import { assignUnassignPermission, fetchPermissions } from '@/store/Admin';
import { assignRole, unassignRole } from '@/store/User';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Admin } from '../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: Admin;
}

const roleOptions = [
  { value: 'admin', label: 'Admin (Full Control)' },
  { value: 'moderator', label: 'Moderator' },
  { value: 'user', label: 'User' },
  { value: 'publisher', label: 'Publisher' },
];

const EditRoleAndPermissionModal: React.FC<Props> = ({
  isOpen,
  onClose,
  user,
}) => {
  const dispatch = useDispatch<any>();
  const { permissionList, loading } = useSelector((state: any) => state.admin);

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  // mount par user ke roles/permissions state me dalna
  useEffect(() => {
    if (isOpen && user) {
      const rolesArray = user.role
        ? user.role.split(',').map((r) => r.trim())
        : [];
      setSelectedRoles(rolesArray);

      const per = user.permission.map((p) => p.id);
      setSelectedPermissions(per || []);

      dispatch(fetchPermissions());
    }
  }, [isOpen, user, dispatch]);

  const toggleRole = (role: string) => {
    setSelectedRoles((prev) => {
      if (prev.includes(role)) {
        // role unselect
        const updated = prev.filter((r) => r !== role);

        // agar moderator remove hua -> permissions clear
        if (role === 'moderator') {
          setSelectedPermissions([]);
        }

        return updated;
      } else {
        // role select
        return [...prev, role];
      }
    });
  };

  const togglePermission = (perm: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm],
    );
  };

  const handleSubmit = async () => {
    const originalRoles = user.role
      ? user.role.split(',').map((r) => r.trim())
      : [];
    const originalPermissions = user.permission.map((p) => p.id) || [];

    // 🔹 Roles diff
    const rolesToAssign = selectedRoles.filter(
      (r) => !originalRoles.includes(r),
    );
    const rolesToUnassign = originalRoles.filter(
      (r) => !selectedRoles.includes(r),
    );

    // 🔹 Permissions diff
    const assignPermissionIds = selectedPermissions.filter(
      (p) => !originalPermissions.includes(p),
    );
    const unassignPermissionIds = originalPermissions.filter(
      (p) => !selectedPermissions.includes(p),
    );

    try {
      // call APIs for roles
      for (const role of rolesToAssign) {
        await dispatch(assignRole({ userId: user.id, roleName: role }));
      }
      for (const role of rolesToUnassign) {
        await dispatch(unassignRole({ userId: user.id, roleName: role }));
      }

      // call API for permissions (only if needed)
      if (assignPermissionIds.length || unassignPermissionIds.length) {
        await dispatch(
          assignUnassignPermission({
            userId: user.id,
            assignPermissionIds,
            unassignPermissionIds,
          }),
        );
      }

      toast.success('Roles & Permissions updated');
      onClose();
    } catch (err) {
      toast.error('Failed to update user roles/permissions');
    }
  };

  if (!isOpen || !user) return null;

return (
  <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
    <div className="bg-white rounded-2xl w-[500px] p-6">
      <h2 className="text-xl font-semibold text-center mb-6">
        Edit Roles & Permissions
      </h2>

      {/* Roles */}
      <div className="bg-gray-50 p-4 rounded-xl mb-4">
        <h3 className="text-sm font-medium mb-3">Roles</h3>
        {roleOptions.map((r) => (
          <label key={r.value} className="flex items-center space-x-2 mb-2">
            <input
              type="checkbox"
              value={r.value}
              checked={selectedRoles.includes(r.value)}
              onChange={() => toggleRole(r.value)}
            />
            <span>{r.label}</span>
          </label>
        ))}
      </div>

      {/* Permissions - animate show/hide */}
      <div
        className={`transition-all duration-500 ease-in-out overflow-hidden ${
          selectedRoles.includes('moderator')
            ? 'max-h-96 opacity-100 mt-4'
            : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-gray-50 p-4 rounded-xl">
          <h3 className="text-sm font-medium mb-3">Permissions</h3>
          {loading ? (
            <p className="text-sm text-gray-500">Loading permissions...</p>
          ) : (
            <div className="flex flex-col space-y-2">
              {permissionList?.data?.map((perm: any) => (
                <label key={perm.key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(perm.id)}
                    onChange={() => togglePermission(perm.id)}
                  />
                  <span>
                    {perm.key.charAt(0).toUpperCase() + perm.key.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-between mt-6">
        <button
          className="px-6 py-2 rounded-full border border-black"
          onClick={onClose}
        >
          Cancel
        </button>
        <button
          className="px-6 py-2 rounded-full bg-[#FF0050] text-white"
          onClick={handleSubmit}
        >
          Save Changes
        </button>
      </div>
    </div>
  </div>
);

};

export default EditRoleAndPermissionModal;
